import { sendSubgraphQuery } from ".";
import { chainDetails } from "../../utils/config";

const generalPoolsField = `id
            address
            poolType
            isPaused
            swapFee
            tokensList
            totalLiquidity
            totalSwapVolume
            totalSwapFee
            totalShares
            security
            securityType
            currency
            collateral
            cficode
            margin
            securityOffered
            balancerManager
            minOrderSize
            owner
            factory
            cutoffTime
            offeringDocs
            amp
            createTime
            minimumOrderSize
            minimumPrice
            swapEnabled
            orderBook
            secondaryTrades{
                amount
                price
                executionDate
                orderType
                orderReference
                counterparty {
                    id
                }
                party {
                    id
                }
            }
            secondaryPreTrades {
                id
                pool {
                    id
                }
                party {
                    id
                }
                counterparty {
                    id
                }
                executionDate
            }
            tokens {
                assetManager
                symbol
                name
                decimals
                index
                address
                oldPriceRate
                priceRate
                balance
                paidProtocolFees
                cashBalance
                managedBalance
            }
            orders {
                id
                pool {
                  id
                  address
                  security
                  currency
                  tokens {
                    symbol
                    name
                    decimals
                    index
                    address
                  }
                  tokensList
                }
                tokenIn {
                    address
                }
                tokenOut {
                    address
                }
                amountOffered
                priceOffered
                orderReference
                creator
                timestamp
            } 
            primarySubscriptions{
                subscription
                price
                executionDate
                assetIn{
                  address
                }
                assetOut{
                  address
                }
              executionDate
            }
            marginOrders {
                id
                pool {
                  id
                  address
                  security
                  currency
                  cficode
                  margin
                  collateral
                  tokensList
                  swapFee
                  tokens {
                    symbol
                    name
                    decimals
                    index
                    address
                  }
                 
                }
                creator
                tokenIn {
                    id
                    symbol
                    name
                    decimals
                    address
                }
                tokenOut {
                    id
                    symbol
                    name
                    decimals
                    address
                }
                amountOffered
                priceOffered
                stoplossPrice
                timestamp
                orderReference
}`;

/**
 *
 * @param {number} chainId
 * @returns {string} latest vault subgraph url
 */
export const getVaultSubgraphUrl = (chainId) => {
  return chainDetails[chainId].vaultSubgraphUrl;
};

/**
 *
 * @param {number} chainId
 * @returns {promise<any[]>} all pools
 */
export const fetchAllPools = async (chainId) => {
  const _invalidPoolIds = chainDetails[chainId]?.invalidPoolIds;
  const invalidPoolIds =
    _invalidPoolIds?.length > 0
      ? JSON.stringify(_invalidPoolIds)
      : JSON.stringify([""]);
  const query = `query {
    pools: pools(
      first: 1000 
      orderBy: createTime
      orderDirection: desc
      where: {
        poolType_in: ["PrimaryIssue", "SecondaryIssue", "MarginIssue"]
        id_not_in: ${invalidPoolIds}
      }
    ) {
       ${generalPoolsField} 
    }
  }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string} poolId
 * @returns {promise<any[]>} pool in an array
 */
export const fetchPoolById = async (chainId, poolId) => {
  const query = `query {
    pools: pools(
      where: {
        id: "${poolId?.toLowerCase()}"
      }
    ) {
       ${generalPoolsField} 
    }
  }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string} securityAddress
 * @param {string} currencyAddress
 * @returns {promise<any[]>} pool in an array
 */
export const fetchPoolBySecurityAndCurrency = async (
  chainId,
  securityAddress,
  currencyAddress,
) => {
  const query = `query {
    pools: pools(
      where: {
         security: "${securityAddress?.toLowerCase()}",
      currency: "${currencyAddress?.toLowerCase()}"
      }
    ) {
       ${generalPoolsField} 
    }
  }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string} marginOrderRef
 * @returns {promise<any[]>} pool in an array
 */
export const fetchPoolByMarginOrderRef = async (chainId, marginOrderRef) => {
  const query = `query {
    pools: pools(
      where: {
        marginOrders_: {orderReference: "${marginOrderRef?.toLowerCase()}"} 
      }
    ) {
       id
    currency
    security
    margin
    collateral
    swapFee
    cficode
    marginOrders{
      tokenIn{
        id
      }
      orderReference
    }
    }
  }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @returns {promise<any[]>} primary pools
 */
export const fetchPrimaryPools = async (chainId) => {
  const query = `query {
        pools: pools( 
          orderBy: createTime
          orderDirection: desc
          where: {
            poolType: "PrimaryIssue"
          }
        ) {
           ${generalPoolsField} 
        }
      }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string} poolAddress
 * @returns {promise<any[]>} primary pools with the address
 */
export const fetchPrimaryPoolByAddress = async (chainId, poolAddress) => {
  const query = `query {
          pools: pools( 
            where: {
              poolType: "PrimaryIssue"
              address: "${poolAddress.toLowerCase()}"
            }
          ) {
             ${generalPoolsField} 
          }
        }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @returns {promise<any[]>} secondary pools
 */
export const fetchSecondaryPools = async (chainId) => {
  const query = `query {
          pools: pools( 
            orderBy: createTime
            orderDirection: desc
            where: {
              poolType: "SecondaryIssue"
            }
          ) {
             ${generalPoolsField} 
          }
        }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string} poolAddress
 * @returns {promise<any[]>} secondary pools with the address
 */
export const fetchSecondaryPoolByAddress = async (chainId, poolAddress) => {
  const query = `query {
            pools: pools( 
              where: {
                poolType: "SecondaryIssue"
                address: "${poolAddress.toLowerCase()}"
              }
            ) {
               ${generalPoolsField} 
            }
          }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @returns {promise<any[]>} margin pools
 */
export const fetchMarginPools = async (chainId) => {
  const query = `query {
            pools: pools( 
              orderBy: createTime
              orderDirection: desc
              where: {
                poolType: "MarginIssue"
              }
            ) {
               ${generalPoolsField} 
            }
          }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string} poolAddress
 * @returns {promise<any[]>} margin pools with the address
 */
export const fetchMarginPoolByAddress = async (chainId, poolAddress) => {
  const query = `query {
              pools: pools( 
                where: {
                  poolType: "MarginIssue"
                  address: "${poolAddress.toLowerCase()}"
                }
              ) {
                 ${generalPoolsField} 
              }
            }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};

/**
 *
 * @param {number} chainId
 * @param {string[]} acceptedPoolTypesArray string formatted array of poolType e.g ["PrimaryIssue", "SecondaryIssue"]
 * @returns {promise<any[]>} all pools
 */
export const fetchCustomPools = async (chainId, acceptedPoolTypesArray) => {
  const query = `query {
    pools: pools( 
      orderBy: createTime
      orderDirection: desc
      where: {
        poolType_in: ${acceptedPoolTypesArray}
      }
    ) {
       ${generalPoolsField} 
    }
  }`;
  return await sendSubgraphQuery(
    getVaultSubgraphUrl(chainId),
    query,
    "pools",
    [],
  );
};
